<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import {
  CustomMetadataService,
  CreateCustomMetadataEntryValueDto,
  type EntityCustomMetadataDto,
  type CreateCustomMetadataEntryStringValueDto,
  type GetCustomMetadataListResponseDto,
  type GetCustomMetadataResponseDto,
  type GetCustomMetadataEntryStringValueResponseDto,
} from '@/api';
import SButton from '@/components/SButton/SButton.vue';

interface CustomMetadataStringSelectorProps {
  metadata: GetCustomMetadataListResponseDto;
  entityCustomMetadata?: EntityCustomMetadataDto;
}
const props = defineProps({
  metadata: null,
  entityCustomMetadata: null
});

type CustomMetadataStringSelectorEmits = {
  (event: 'update', metadataId: string, entries: GetCustomMetadataEntryStringValueResponseDto[]): void,
}
const emits = defineEmits(["update"]);

let loading = _ref<boolean>(false);
// Local v-model proxy
let selectedEntry = _ref<GetCustomMetadataEntryStringValueResponseDto | null>(null);
let fetchedMetadata = _ref<GetCustomMetadataResponseDto>();
let searchString = _ref<string>('');

const selectorItems = _computed(() => {
  if (fetchedMetadata.value) {
    return fetchedMetadata.value.entries.data as GetCustomMetadataEntryStringValueResponseDto[];
  }
  return [];
});

const searchStringExistsInEntries = _computed(() => {
  if (searchString.value) {
    return selectorItems.value.some((item) => item.value.toLowerCase() === searchString.value.toLowerCase());
  }
  return false;
});

/**
 * Select entry: trigger change event
 * @param entry
 */
const selectEntries = (entries: GetCustomMetadataEntryStringValueResponseDto[]) => {
  emits('update', props.metadata.id, entries);
};

/**
 * Clear selected entry
 */
const onClear = () => {
  selectedEntry.value = null;
  searchString.value = '';
};

/**
 * Get custom metadata detailed infos
 * @param metadataId
 */
const fetchMetadataInfos = (metadataId: string, selectableEntryValue?: string): Promise<void> => {
  loading.value = true;
  return CustomMetadataService.getCustomMetadata({ id: metadataId })
      .then((response: GetCustomMetadataResponseDto) => {
        fetchedMetadata.value = response;
        if (selectableEntryValue) {
          const data = fetchedMetadata.value.entries.data as GetCustomMetadataEntryStringValueResponseDto[];
          const selectableEntry = data.find((item) => item.value === selectableEntryValue);
          if (selectableEntry) {
            selectEntries([selectableEntry]);
          }
        }
      })
      .finally(() => {
        loading.value = false;
      });
};

/**
 * Handle creation of a new entry
 * Post creation request, fetch the metadata again to get the new entry selected
 */
 const onCreateNewEntry = () => {
  if (searchString.value) {
    const creatableString = searchString.value;
    const newEntry = {
      type: CreateCustomMetadataEntryValueDto.type.STRING,
      value: creatableString,
    } as CreateCustomMetadataEntryStringValueDto;
    loading.value = true;
    CustomMetadataService.createCustomMetadataEntry({
      customMetadataId: props.metadata.id,
      requestBody: newEntry,
    })
    .then(() => {
      // Fetch the metadata again to get the new entry selected
      fetchMetadataInfos(props.metadata.id, creatableString);
    })
    .finally(() => {
      loading.value = false;
    });
  }
};

watch(() => props.metadata, (newMetadata) => {
  if (newMetadata) {
    fetchMetadataInfos(newMetadata.id);
  }
}, { immediate: true });

// Pre-set selected entries based on the entity custom metadata
watch(() => props.entityCustomMetadata, () => {
  if (props.entityCustomMetadata) {
    // Strings metadata cannot be multiple: only take first entry
    const [firstEntry] = props.entityCustomMetadata.entries as GetCustomMetadataEntryStringValueResponseDto[];
    selectedEntry.value = firstEntry || null;
  }
}, { immediate: true });

watch(() => selectedEntry.value, (newSelectedEntry) => {
  if (newSelectedEntry) {
    selectEntries([newSelectedEntry]);
    searchString.value = '';
  } else {
    selectEntries([]);
  }
}, { deep: true, immediate: true });

</script>
<template lang="pug">
.custom-metadata-string-selector
    v-autocomplete.mt-1(
      :disabled="!fetchedMetadata || loading"
      :items="selectorItems"
      :loading="loading"
      :placeholder="$t('custom_metadata.selectors.string_placeholder', { name: props.metadata.name.toLowerCase() })"
      :return-object="true"
      :search-input.sync="searchString"
      @blur="searchString=''"
      @click:append="onClear"
      clearable
      data-track="custom-metadata-string-selector"
      dense
      hide-details
      item-text="value"
      item-value="id"
      label=""
      outlined
      v-bind='$attrs'
      v-model="selectedEntry"
      v-on='$listeners'
    )

      template( v-slot:no-data )
        //- Empty slot: don't show any message, but allow the append-item one to be displayed
        span

      template( v-slot:append-item )
        SButton(
          v-if="searchString && !searchStringExistsInEntries"
          class="ml-2"
          icon="icon-add"
          @click="onCreateNewEntry"
          variant="outlined"
          :text="$t('custom_metadata.selectors.string_create_label', { name: searchString })"
          color="secondary"
          data-track="custom-metadata-string-selector-create-new-entry"
        )

</template>
